import React, { useState, useEffect } from 'react';
import { Input, Spin, Segmented, Alert } from 'antd';
import { SearchOutlined, AppstoreOutlined, TableOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as API from '../services/api';
import { useThemeStyles } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import {
  ProjectCardGrid,
  ProjectCardPagination,
  ProjectTableView,
} from '../components/ProjectDirectoryViews';
import CreateProjectFab from '../components/CreateProjectFab';
import PageHeader from '../components/PageHeader';
import StarterKitIssuanceModal from '../components/StarterKitIssuanceModal';
import { useStarterKitIssuance } from '../starter_kit/useStarterKitIssuance';
import { pageShellStyle } from '../theme';

const Landing = () => {
  const navigate = useNavigate();
  const themeStyles = useThemeStyles();
  const { token, isAdmin, isSuperAdmin } = useAuth();
  const isGroupAdmin = isAdmin && !isSuperAdmin;
  const isEndUser = !isAdmin;
  const starterKit = useStarterKitIssuance(token);
  const [searchText, setSearchText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [projectLayout, setProjectLayout] = useState('cards');
  const pageSize = 12;
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (token) {
      starterKit.refreshEntitlements();
    }
  }, [token, starterKit.refreshEntitlements]);

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    const fetchProjects = async () => {
      setLoading(true);
      try {
        if (!token) {
          if (alive) setProjects([]);
          return;
        }
        const response = await API.listRbacProjects(token, null, { signal: controller.signal });
        const data = Array.isArray(response.data) ? response.data : [];
        const projectsWithStatus = data.map((p) => {
          const meta = p.meta || {};
          const deployedFromMeta =
            meta.embedding_status === 'completed' && meta.vector_status === 'completed';
          return {
            ...p,
            created_at: meta.created_at || meta.updated_at || null,
            embedding_status: meta.embedding_status || 'pending',
            vector_status: meta.vector_status || 'pending',
            deleted: meta.deleted || false,
            deployed: p.chat_available != null ? Boolean(p.chat_available) : deployedFromMeta,
          };
        }).filter((p) => !p.deleted);
        if (alive) setProjects(projectsWithStatus);
      } catch (error) {
        if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
          console.error('Error fetching projects:', error);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchProjects();
    return () => {
      alive = false;
      controller.abort();
    };
  }, [token]);

  const filteredProjects = (() => {
    const q = (searchQuery || '').toLowerCase().trim();
    let list = projects.filter((p) => {
      if (isSuperAdmin) {
        const groupLabel = String(p.group_name || p.group_id || '').toLowerCase();
        return !q || groupLabel.includes(q);
      }
      const isIdSearch = q.includes('proj_');
      const matchesSearch = isIdSearch
        ? String(p.project_id || '').toLowerCase().includes(q)
        : String(p.name || '').toLowerCase().includes(q);
      return matchesSearch;
    });
    if (isSuperAdmin) {
      list = [...list].sort((a, b) => {
        const ga = String(a.group_name || a.group_id || '').toLowerCase();
        const gb = String(b.group_name || b.group_id || '').toLowerCase();
        const byGroup = ga.localeCompare(gb);
        if (byGroup !== 0) return byGroup;
        return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
      });
    }
    return list;
  })();

  const startIndex = (currentPage - 1) * pageSize;
  const paginatedProjects = filteredProjects.slice(startIndex, startIndex + pageSize);

  const openProject = (project) => {
    navigate(`/project/${project.project_id}?tab=file_pipeline`);
  };

  const openChat = (project) => {
    navigate(`/chat/${project.project_id}`);
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ ...pageShellStyle, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <PageHeader
          title="Dashboard"
          actions={(
            <Segmented
              value={projectLayout}
              onChange={(value) => {
                setProjectLayout(value);
                setCurrentPage(1);
              }}
              options={[
                { label: 'Cards', value: 'cards', icon: <AppstoreOutlined /> },
                { label: 'Table', value: 'table', icon: <TableOutlined /> },
              ]}
            />
          )}
        />
        <div style={{ marginBottom: 16, width: '100%' }}>
          <Input
            placeholder={isSuperAdmin ? 'Search groups...' : 'Search projects...'}
            prefix={<SearchOutlined style={{ color: themeStyles.textTertiary }} />}
            style={{ width: '100%', maxWidth: 560 }}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            allowClear
            onClear={() => {
              setSearchQuery('');
              setSearchText('');
              setCurrentPage(1);
            }}
          />
        </div>

        {!loading && isEndUser && projects.length === 0 && (
          <Alert
            type="info"
            showIcon
            message="You have no projects assigned. Contact your admin."
            style={{ marginBottom: 16 }}
          />
        )}

        {!loading && isGroupAdmin && projects.length === 0 && (
          <Alert
            type="info"
            showIcon
            message="You have not created any projects yet."
            description="Use the + button to create your first project and manage it from this dashboard."
            style={{ marginBottom: 16 }}
          />
        )}

        {!loading && isSuperAdmin && projects.length === 0 && (
          <Alert
            type="info"
            showIcon
            message="There are no projects to display yet."
            description="Projects created by group admins will appear here once they exist."
            style={{ marginBottom: 16 }}
          />
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Spin size="large" />
          </div>
        ) : projects.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, gap: '24px' }}>
            {isEndUser && (
              <div style={{ fontSize: '15px', color: themeStyles.textSecondary, textAlign: 'center', maxWidth: 420 }}>
                When an administrator adds you to a project, it will show up on this dashboard.
              </div>
            )}
            {isGroupAdmin && (
              <div style={{ fontSize: '15px', color: themeStyles.textSecondary, textAlign: 'center', maxWidth: 420 }}>
                Create a project with the + button in the corner to start uploading documents and using chat.
              </div>
            )}
          </div>
        ) : filteredProjects.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, gap: '24px' }}>
            <div style={{ fontSize: '18px', color: themeStyles.textSecondary }}>No results found</div>
          </div>
        ) : projectLayout === 'table' ? (
          <ProjectTableView
            projects={filteredProjects}
            themeStyles={themeStyles}
            onOpenProject={openProject}
            showAdminColumn={isSuperAdmin}
            onDownloadStarterKit={starterKit.startIssuance}
            starterKitBusyProjectId={starterKit.creatingProjectId}
            getProjectKitState={starterKit.getProjectKitState}
            getProjectKitIconVisualState={starterKit.getProjectKitIconVisualState}
            getStarterKitIconTooltip={starterKit.getStarterKitIconTooltip}
            pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
          />
        ) : (
          <>
            <ProjectCardGrid
              projects={paginatedProjects}
              themeStyles={themeStyles}
              onOpenProject={openProject}
              onOpenChat={openChat}
              onDownloadStarterKit={starterKit.startIssuance}
              starterKitBusyProjectId={starterKit.creatingProjectId}
              getProjectKitState={starterKit.getProjectKitState}
              getProjectKitIconVisualState={starterKit.getProjectKitIconVisualState}
              getStarterKitIconTooltip={starterKit.getStarterKitIconTooltip}
              showChatShortcut={!isSuperAdmin}
              showAdmin={isSuperAdmin}
            />
            <ProjectCardPagination
              current={currentPage}
              total={filteredProjects.length}
              pageSize={pageSize}
              onChange={setCurrentPage}
            />
          </>
        )}
      </div>
      <StarterKitIssuanceModal
        open={starterKit.modalOpen}
        entitlement={starterKit.entitlement}
        isRegenerateFlow={starterKit.isRegenerateFlow}
        downloading={starterKit.downloading}
        onCancel={starterKit.closeModal}
        onDownload={starterKit.downloadIssuedKit}
      />
      <CreateProjectFab />
    </div>
  );
};

export default Landing;
